import { Table, Text } from "@ttc3k/trekker";
import {
  formatPrice,
  getTaxLineLabel,
  type TaxLineDisplay,
} from "shared/utils";
import type {
  CartItem as CartItemType,
  PaymentRequestLineItem,
} from "widgets/booker/contexts";
import { Fragment, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CartItem } from "./CartItem";
import {
  DiscountItem,
  type DiscountItem as DiscountItemType,
} from "./DiscountItem";
import {
  DepositSettings,
  EnumScheduledPaymentStatus,
  EnumScheduledPaymentType,
  EnumStripeMetadataInvoiceStatus,
  Product,
  useScheduledPaymentFindOneQuery,
} from "gql/generated";
import type { MultilingualLangKey } from "shared/types";

type CheckoutSummaryProps = {
  subTotal: number;
  tax: number;
  /**
   * Per-tax-rate breakdown (e.g. GST + PST). When provided, one row per line
   * is rendered with the configured name/rate; otherwise a single "Tax" row
   * with a rate back-calculated from `tax / subTotal` (legacy data).
   */
  taxLines?: TaxLineDisplay[] | null;
  customerFee: number;
  grandTotal: number;
  cart: CartItemType[];
  isReservationPage?: boolean;
  orderId?: string;
  discounts: DiscountItemType[];
  depositSettings?: DepositSettings | null;
  depositTotal: number;
  /**
   * Approximate payment-processing fees already built into the prices (cents).
   * When > 0, a small note under the total says no extra fee is added.
   */
  includedFeesEstimate?: number;
  /** Null hides the row (no delivery chosen, or a non-shipping order); 0 renders "Free". */
  shippingTotal?: number | null;
  isManualOrder?: boolean;
  isEftPending?: boolean;
  manualOrderInvoiceStatus?: EnumStripeMetadataInvoiceStatus;
  mainProduct?: Product;
  additionalLineItems?: PaymentRequestLineItem[];
  /** When true, the customer claimed a tax exemption at checkout, so tax is $0.00. */
  taxExemptionClaimed?: boolean;
  /** SilkStart-imported orders: show only total and amount paid on the receipt. */
  isLegacyOrder?: boolean;
  /** Cents written off as complimentary; shown separately from the amount paid. */
  complimentaryAmount?: number;
  /**
   * Blank invoices carry a hidden $0 placeholder product just to satisfy
   * schema requirements — hide its row (and any product discounts/upsells)
   * since only the line items are meaningful to the recipient.
   */
  hideMainProduct?: boolean;
  /** Selected fee / renewable / merch option for the main cart product. */
  selectedTierId?: string | null;
  language?: MultilingualLangKey;
  /** Replaces a cart row (e.g. an editable merch line); return undefined to keep the default. */
  renderCartItem?: (item: CartItemType) => ReactNode | undefined;
};

export const CheckoutSummary = ({
  cart,
  grandTotal,
  subTotal,
  customerFee,
  tax,
  taxLines,
  isReservationPage = false,
  orderId,
  discounts,
  depositSettings,
  depositTotal,
  includedFeesEstimate = 0,
  shippingTotal = null,
  isManualOrder = false,
  isEftPending = false,
  manualOrderInvoiceStatus,
  mainProduct,
  additionalLineItems = [],
  taxExemptionClaimed = false,
  isLegacyOrder = false,
  complimentaryAmount = 0,
  hideMainProduct = false,
  selectedTierId,
  language,
  renderCartItem,
}: CheckoutSummaryProps) => {
  const { t } = useTranslation("booker", { keyPrefix: "CHECKOUT.SUMMARY" });

  const mainProductDiscounts = discounts.filter(d => d.isMainProductOnly);
  const allProductDiscounts = discounts.filter(d => !d.isMainProductOnly);

  // Legacy fallback when no server tax lines exist: derive the effective tax
  // rate from the amounts so the label can show it (e.g. "Tax (13%)").
  // Rounded to 2 decimals with trailing zeros trimmed.
  const taxRatePercent =
    subTotal > 0 && tax > 0
      ? parseFloat(((tax / subTotal) * 100).toFixed(2))
      : null;
  const hasTaxLines = !!taxLines && taxLines.length > 0;

  const { data: scheduledPayments } = useScheduledPaymentFindOneQuery({
    variables: {
      filter: {
        order: orderId,
        type: EnumScheduledPaymentType.RemainingBalance,
      },
    },
    skip: !isReservationPage || !orderId,
  });

  const isRemainingBalanceChargedSuccessfully =
    depositSettings?.enabled &&
    scheduledPayments?.scheduledPaymentFindOne &&
    scheduledPayments?.scheduledPaymentFindOne.status ===
      EnumScheduledPaymentStatus.Success;

  const amountCurrentlyPaid =
    isRemainingBalanceChargedSuccessfully ||
    !depositSettings?.enabled ||
    isManualOrder
      ? grandTotal
      : depositTotal;

  const isManualOrderPaid =
    isManualOrder &&
    manualOrderInvoiceStatus &&
    manualOrderInvoiceStatus === EnumStripeMetadataInvoiceStatus.Paid;

  return (
    // If the receipt is taller than a printed PDF page, break between rows
    // rather than through one.
    <Table.Root css={{ "& tr": { breakInside: "avoid", pageBreakInside: "avoid" } }}>
      {!isReservationPage && (
        <Table.Caption color={"text.light!"}>{t("TITLE")}</Table.Caption>
      )}
      <Table.Body>
        {!isLegacyOrder && !hideMainProduct && cart.length > 0 && (
          <>
            {/* Main Product */}
            {renderCartItem?.(cart[0]) ?? (
              <CartItem
                item={cart[0]}
                isReservationPage={isReservationPage}
                mainProduct={mainProduct}
                selectedTierId={selectedTierId}
                language={language}
              />
            )}

            {/* Main Product Discounts */}
            {mainProductDiscounts.map(discount => (
              <DiscountItem
                key={discount.label}
                discountItem={discount}
                isReservationPage={isReservationPage}
              />
            ))}

            {/* Upsell Products */}
            {cart.slice(1).map(item => (
              <Fragment key={item.productId}>
                {renderCartItem?.(item) ?? (
                  <CartItem
                    item={item}
                    isReservationPage={isReservationPage}
                    language={language}
                  />
                )}
              </Fragment>
            ))}

            {/* All Product Discounts */}
            {allProductDiscounts.map(discount => (
              <DiscountItem
                key={discount.label}
                discountItem={discount}
                isReservationPage={isReservationPage}
              />
            ))}
          </>
        )}
        {!isLegacyOrder &&
          additionalLineItems.map((item, idx) => (
            <Table.Row key={`${item.description}-${idx}`}>
              <Table.Header paddingRight={"150"}>
                {item.description}
                {item.quantity > 1 ? ` x${item.quantity}` : ""}
              </Table.Header>
              <Table.Cell color={item.amount < 0 ? "text.success.dark" : undefined} whiteSpace="nowrap">
                ${formatPrice(item.amount * item.quantity)}
              </Table.Cell>
            </Table.Row>
          ))}
        {!isLegacyOrder && (
          <>
        <Table.Row>
          <Table.Header>{t("SUBTOTAL")}</Table.Header>
          <Table.Cell whiteSpace="nowrap">${formatPrice(subTotal)}</Table.Cell>
        </Table.Row>
        {shippingTotal != null && (
          <Table.Row>
            <Table.Header>{t("SHIPPING")}</Table.Header>
            <Table.Cell whiteSpace="nowrap">
              {shippingTotal > 0 ? `$${formatPrice(shippingTotal)}` : t("FREE")}
            </Table.Cell>
          </Table.Row>
        )}
        {hasTaxLines ? (
          taxLines!.map((line, idx) => (
            <Table.Row key={`tax-${idx}`}>
              <Table.Header>
                {getTaxLineLabel(line, t("TAX"))}
                {taxExemptionClaimed && idx === taxLines!.length - 1 && (
                  <Text color={"text.mid"} visual={"smallRegular"}>
                    {t("TAX_EXEMPTION_CLAIMED")}
                  </Text>
                )}
              </Table.Header>
              <Table.Cell whiteSpace="nowrap">
                ${formatPrice(line.amount)}
              </Table.Cell>
            </Table.Row>
          ))
        ) : (
          <Table.Row>
            <Table.Header>
              {t("TAX")}
              {taxRatePercent != null ? ` (${taxRatePercent}%)` : ""}
              {taxExemptionClaimed && (
                <Text color={"text.mid"} visual={"smallRegular"}>
                  {t("TAX_EXEMPTION_CLAIMED")}
                </Text>
              )}
            </Table.Header>
            <Table.Cell whiteSpace="nowrap">${formatPrice(tax)}</Table.Cell>
          </Table.Row>
        )}
        {customerFee > 0 && (
          <Table.Row>
            <Table.Header>{t("FEE")}</Table.Header>
            <Table.Cell whiteSpace="nowrap">${formatPrice(customerFee)}</Table.Cell>
          </Table.Row>
        )}
          </>
        )}
        <Table.Row
          // If the order is a manual order and the invoice is not paid,
          // or if the deposit is not enabled and the order is not being
          // displayed on the reservation page, then don't show the border
          border={
            (
              isEftPending ||
              (isManualOrder
                ? !isManualOrderPaid
                : !depositSettings?.enabled && !isReservationPage)
            )
              ? "none"
              : undefined
          }
        >
          <Table.Header>{t("TOTAL")}</Table.Header>
          <Table.Cell whiteSpace="nowrap">${formatPrice(grandTotal)}</Table.Cell>
        </Table.Row>
        {includedFeesEstimate > 0 && !isLegacyOrder && (
          <Table.Row border={"none"}>
            <Table.Header colSpan={2}>
              <Text color={"text.mid"} visual={"smallRegular"}>
                {t("INCLUDED_FEES", {
                  amount: formatPrice(includedFeesEstimate),
                })}
              </Text>
            </Table.Header>
          </Table.Row>
        )}
        {depositSettings?.enabled && !isManualOrder && !isLegacyOrder && (
          <>
            <Table.Row>
              <Table.Header>{t("DEPOSIT")}</Table.Header>
              <Table.Cell whiteSpace="nowrap">${formatPrice(depositTotal)}</Table.Cell>
            </Table.Row>
            <Table.Row border={!isReservationPage ? "none" : undefined}>
              <Table.Header>
                {t("REMAINING")}
                <Text color={"text.mid"} visual={"smallRegular"}>
                  {depositSettings.chargeBalanceDaysBeforeCheckIn > 0
                    ? t("REMAINING_BALANCE_MESSAGE", {
                        days: depositSettings.chargeBalanceDaysBeforeCheckIn,
                      })
                    : t("REMAINING_BALANCE_MESSAGE_CHECKIN")}
                </Text>
              </Table.Header>
              <Table.Cell whiteSpace="nowrap">${formatPrice(grandTotal - depositTotal)}</Table.Cell>
            </Table.Row>
          </>
        )}
        {isReservationPage &&
          !isEftPending &&
          (isManualOrder ? isManualOrderPaid : true) && (
          <>
            {complimentaryAmount > 0 && (
              <Table.Row>
                <Table.Header>{t("COMPLIMENTARY")}</Table.Header>
                <Table.Cell color="text.success.dark" whiteSpace="nowrap">
                  -${formatPrice(complimentaryAmount)}
                </Table.Cell>
              </Table.Row>
            )}
            <Table.Row border={"none"}>
              <Table.Header>{t("AMOUNT_PAID")}</Table.Header>
              <Table.Cell whiteSpace="nowrap">
                $
                {formatPrice(
                  Math.max(0, amountCurrentlyPaid - complimentaryAmount),
                )}
              </Table.Cell>
            </Table.Row>
          </>
        )}
      </Table.Body>
    </Table.Root>
  );
};
